'use client'

import { motion, useTransform, useScroll, useMotionValueEvent } from "framer-motion";
import { PortableTextBlock } from "next-sanity";
import React, { useRef, useState, useEffect } from "react";
import Image from "next/image";

import { PageQueryResult } from "@/sanity.types";
import ColorScheme from "@/components/colorScheme";
import CustomPortableText from "@/components/portable-text";
import useMediaQuery from "@/hooks/useMediaQuery";

import styles from "./PinnedHorizontalCarousel.module.css";

type PinnedHorizontalCarouselProps = Extract<NonNullable<NonNullable<PageQueryResult>["content"]>[number], { _type: "pinnedHorizontalCarousel" }>;

export default function PinnedHorizontalCarousel(props: PinnedHorizontalCarouselProps) {
  const { colorScheme, carouselItems } = props;

  const isDesktop = useMediaQuery("(min-width: 768px)");

  const targetRef = useRef<HTMLDivElement | null>(null);

  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start start", "end end"],
  });

  const endProgress = carouselItems && carouselItems.length > 0 ? (carouselItems.length - 1) * 100 : 0;

  const [activeIndex, setActiveIndex] = useState(0);
  const count = carouselItems?.length ?? 0;

  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // The sticky panel is a full screen tall with the cards centered in it, so
  // the panel's last screen always ends with (screen - card) / 2 of empty
  // background under the final card. Measure that leftover and pull the next
  // section up by the same amount so the page continues right below the card
  // instead of after a viewport-dependent stretch of dead space.
  const [tailHeight, setTailHeight] = useState(0);

  useEffect(() => {
    if (!isDesktop) {
      setTailHeight(0);
      return;
    }

    const lastCard = itemRefs.current[count - 1];
    if (!lastCard) return;

    const measure = () => {
      const cardHeight = lastCard.getBoundingClientRect().height;
      setTailHeight(Math.max(0, (window.innerHeight - cardHeight) / 2));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(lastCard);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isDesktop, count]);

  useEffect(() => {
    if (isDesktop) return;
    const observers = itemRefs.current.map((el) => {
      if (!el) return null;
      const observer = new ResizeObserver(([entry]) => {
        el.style.setProperty('--mobile-height', `${entry.contentRect.height}px`);
      });
      observer.observe(el);
      return observer;
    });
    return () => { observers.forEach((obs) => obs?.disconnect()); };
  }, [isDesktop, count]);

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    const index = Math.min(Math.floor(latest * count), count - 1);
    setActiveIndex(index);
  });

  const x = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", `-${endProgress}%`]
  );

  return (
    <section
      className={styles.pinnedHorizontalCarousel}
      style={{ marginBottom: tailHeight ? -tailHeight : undefined }}
    >
      <ColorScheme colorScheme={colorScheme}>
        <div className="bg-(--color-background) text-(--color-foreground)">
          <div
            ref={targetRef}
            className="wrapper relative"
            style={{
              height: isDesktop ? `${(carouselItems?.length ?? 0) * 100 * 2}vh` : "auto"
            }}
          >
            <div className={isDesktop ? "md:sticky md:top-0 h-screen overflow-hidden grid" : ""}>
              <motion.div
                style={{
                  x: isDesktop ? x : "0"
                }}
                className={isDesktop ? "flex gap-4 px-2 h-full w-228.75 self-center box-content mx-auto" : "flex flex-col gap-5"}
              >
                {carouselItems && carouselItems.length > 0 ? (
                  carouselItems.map((item, index) => {
                    const {colorScheme, content, image} = item;

                    return (
                      <div key={index} ref={(el) => { itemRefs.current[index] = el; }} className={`${styles.item} item md:basis-228.75 group max-w-full flex-none self-center static md:static  ${isDesktop && index === activeIndex ? styles.active : ''}`} style={{ '--index': carouselItems.length - index } as React.CSSProperties}>
                        <ColorScheme colorScheme={colorScheme && colorScheme != 'default'? colorScheme : null}>
                          <div className="self-center flex flex-col md:flex-row-reverse items-stretch md:items-start pt-5 px-5 pb-15 mx-5 md:m-0 md:p-7.5 gap-7.5 min-h-125 md:min-h-auto bg-(--color-background) text-(--color-foreground) overflow-hidden rounded-[10px]">
                            {image && image.assetPath &&
                              <div className={`${styles.itemImageWrapper} transition-all duration-800 basis-sm shrink-0 grow-0 md:flex-[1 1 327px]`}>
                                <Image
                                  src={image.assetPath}
                                  alt={image.caption || ""}
                                  fill={true}
                                  loading="lazy"
                                  sizes="(min-width: 768px) 351px, calc(100vw - 80px)"
                                  className="object-cover"
                                />
                              </div>
                            }
                            <div className="rte rte--pinned-carousel md:basis-[]">
                              <CustomPortableText value={content as PortableTextBlock[]} />
                            </div>
                          </div>
                        </ColorScheme>
                      </div>
                    )
                  })
                ) : (
                  <p>No carousel items available.</p>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </ColorScheme>
    </section>
  )
}
